import { defineStore } from "pinia";
import axios from "axios";
import { fetchUser, viewedElement } from "../../api/v2/user.ts";
import { get } from "@vueuse/core";

export const useUserStore = defineStore("user", {
  state: () => {
    return {
      id: null,
      email: null,
      accessToken: null,
      refreshToken: null,
      accessTokenExpiresAt: null,
      authorizationCodeVerifier: null,
      language: null,
      clientCode: null,
      isVerified: null,
      avatar: null,
      assistantName: null,
      username: null,
      externalUserId: null,
      assessmentAnswersCount: null,
      firstName: null,
      scoutPolicyAcceptedAt: null,
      communityPolicyAcceptedAt: null,
      isSbkUser: null,
      viewedElements: [],
      passwordChangedAt: null,
      isOptedIn: null,
      trackingAcceptedAt: null,
      canBypassMaintenance: false,
    };
  },
  actions: {
    setId(id) {
      this.id = id;
    },
    setEmail(email) {
      this.email = email;
    },
    setAccessToken(accessToken) {
      this.accessToken = accessToken;
      if (accessToken) {
        axios.defaults.headers.common = {
          Authorization: `Bearer ${accessToken}`,
        };
      }
    },
    setRefreshToken(refreshToken) {
      this.refreshToken = refreshToken;
    },
    setAuthorizationCodeVerifier(authorizationCodeVerifier) {
      this.authorizationCodeVerifier = authorizationCodeVerifier;
    },
    setLanguage(language) {
      if (!language) {
        language = this.UserManager.getDefaultLanguage();
      }

      this.language = language;
    },
    setClientCode(clientCode) {
      this.clientCode = clientCode;
    },
    setAccessTokenExpiresAt(accessTokenExpiresAt) {
      this.accessTokenExpiresAt = accessTokenExpiresAt;
    },
    setIsVerified(isVerified) {
      this.isNotVerified = isVerified;
    },
    setAssistantName(assistantName) {
      this.assistantName = assistantName;
    },
    setAvatar(avatar) {
      if (!avatar) {
        avatar = "AVATAR_NONE";
      }

      this.avatar = avatar;
    },
    setUsername(username) {
      this.username = username;
    },
    setExternalUserId(externalUserId) {
      this.externalUserId = externalUserId;
    },
    setFirstname(firstname) {
      this.firstName = firstname;
    },
    setLastname(lastname) {
      this.lastName = lastname;
    },
    setScoutPolicyAcceptedAt(scoutPolicyAcceptedAt) {
      this.scoutPolicyAcceptedAt = scoutPolicyAcceptedAt;
    },
    setCommunityPolicyAcceptedAt(communityPolicyAcceptedAt) {
      this.communityPolicyAcceptedAt = communityPolicyAcceptedAt;
    },
    setIsSbkUser(isSbkUser) {
      this.isSbkUser = isSbkUser;
    },
    setPasswordChangedAt(passwordChangedAt) {
      this.passwordChangedAt = passwordChangedAt;
    },
    setViewedElements(viewedElements) {
      this.viewedElements = viewedElements;
    },
    setTrackingAcceptedAt(trackingAcceptedAt) {
      this.trackingAcceptedAt = trackingAcceptedAt;
    },
    setCanBypassMaintenance(canBypassMaintenance) {
      this.canBypassMaintenance = canBypassMaintenance === true;
    },
    setPushtoken(pushToken) {
      this.pushToken = pushToken;
    },
    hasViewedElement(elementKey) {
      return (
        this.viewedElements.find((element) => {
          return element.elementIdentifier === elementKey;
        }) !== undefined
      );
    },
    setIsOptedIn(isOptedIn) {
      this.isOptedIn = isOptedIn;
    },
    addViewedElement(elementKey) {
      this.viewedElements.push({ elementIdentifier: elementKey });
    },
    async getUser() {
      if (!this.id) {
        try {
          const response = await fetchUser();
          if (response.data) {
            this.setId(response.data.id);
            this.setClientCode(response.data.clientCode);
            this.setExternalUserId(response.data.externalId);
            this.setUsername(response.data.username);
            this.setIsSbkUser(response.data.isSbkUser);
            this.setPasswordChangedAt(response.data.passwordChangedAt);
            this.setViewedElements(response.data.viewedElements);
            this.setPushtoken(response.data.pushToken);
            this.setAvatar(
              response.data.avatar
                ? this.formatAvatarString(response.data.avatar)
                : null
            );
            this.setIsVerified(response.data.isNotVerified);
            this.setScoutPolicyAcceptedAt(response.data.scoutPolicyAcceptedAt);
            this.setCommunityPolicyAcceptedAt(
              response.data.communityPolicyAcceptedAt
            );
            this.setIsOptedIn(response.data.isOptedIn);
            this.setTrackingAcceptedAt(response.data.trackingAcceptedAt);
            this.setCanBypassMaintenance(response.data.canBypassMaintenance);
          }
        } catch (error) {
          // If the user is not authenticated, clear tokens
          if (error.response && error.response.status === 401) {
            this.setAccessToken(null);
            this.setRefreshToken(null);
          }

          return error;
        }
      }

      return this.id;
    },
    formatAvatarString(rawAvatar) {
      return rawAvatar.replace("AVATAR_", "").toLowerCase();
    },
    async saveElementViewed(viewedElementId) {
      await viewedElement(viewedElementId);
      this.addViewedElement(viewedElementId);
    },
  },
  getters: {
    getId(state) {
      return state.id;
    },
    getEmail(state) {
      return state.email;
    },
    getAccessToken(state) {
      return state.accessToken;
    },
    getRefreshToken(state) {
      return state.refreshToken;
    },
    getAuthorizationCodeVerifier(state) {
      return state.authorizationCodeVerifier;
    },
    getAccessTokenExpiresAt(state) {
      return state.accessTokenExpiresAt;
    },
    getLanguage(state) {
      return state.language;
    },
    getClientCode(state) {
      return state.clientCode;
    },
    getIsVerified(state) {
      return state.isNotVerified;
    },
    getAssistantName(state) {
      return state.assistantName;
    },
    getAvatar(state) {
      return state.avatar === null || state.avatar === "none"
        ? "phileo"
        : state.avatar;
    },
    getUsername(state) {
      return state.username ?? "Buddy";
    },
    getPushToken(state) {
      return state.pushToken;
    },
    getShowWellbeingCheck(state) {
      return state.showWellbeingCheck;
    },
    getExternalUserId(state) {
      return state.externalUserId;
    },
    getFirstname(state) {
      return state.firstName;
    },
    getLastname(state) {
      return state.lastName;
    },
    getScoutPolicyAcceptedAt(state) {
      return state.scoutPolicyAcceptedAt;
    },
    getCommunityPolicyAcceptedAt(state) {
      return state.communityPolicyAcceptedAt;
    },
    getIsSbkUser(state) {
      return state.isSbkUser;
    },
    getViewedElements(state) {
      return state.viewedElements;
    },
    getPasswordChangedAt(state) {
      return state.passwordChangedAt;
    },
    getIsOptedIn(state) {
      return state.isOptedIn;
    },
    getTrackingAcceptedAt(state) {
      return state.trackingAcceptedAt;
    },
    getCanBypassMaintenance(state) {
      return state.canBypassMaintenance;
    },
  },
  persist: {
    key: "oauth2_tokens",
    storage: window.localStorage,
    pick: [
      "accessToken",
      "refreshToken",
      "accessTokenExpiresAt",
      "authorizationCodeVerifier",
    ],
    beforeHydrate: (context) => {},
    afterHydrate: (context) => {
      // Set accessToken for axios
      context.store.setAccessToken(context.store.accessToken);
    },
  },
});
